// /status — live service status, fed by /api/status (a same-origin proxy over
// the Uptime Kuma status-page feed). Auto-refreshes once a minute.

import { useCallback, useEffect, useId, useState } from 'react';
import { RefreshCw } from 'lucide-react';

interface Beat {
  status: number; // 0 down · 1 up · 2 pending · 3 maintenance
  time: string;
  ping: number | null;
}

interface MonitorStatus {
  name: string;
  status: number;
  ping: number | null;
  uptime24: number | null;
  beats: Beat[];
}

interface StatusGroup {
  name: string;
  monitors: MonitorStatus[];
}

interface StatusPayload {
  overall: 'up' | 'degraded' | 'down';
  updatedAt: string;
  groups: StatusGroup[];
}

const TICKS = 40;

const HEADLINE: Record<StatusPayload['overall'], { lead: string; accent: string }> = {
  up: { lead: 'All systems', accent: 'operational.' },
  degraded: { lead: 'Partial', accent: 'disruption.' },
  down: { lead: 'Major', accent: 'outage.' },
};

const STATE_COLOR: Record<StatusPayload['overall'], string> = {
  up: 'bg-success',
  degraded: 'bg-warning',
  down: 'bg-error',
};

const STATE_LABEL: Record<StatusPayload['overall'], string> = {
  up: 'Live',
  degraded: 'Degraded',
  down: 'Outage',
};

function beatColor(status: number): string {
  if (status === 1) return 'bg-success/70';
  if (status === 0) return 'bg-error';
  return 'bg-warning/70';
}

function formatPct(fraction: number | null): string {
  if (fraction === null) return '–';
  const pct = fraction * 100;
  return `${pct >= 100 ? '100' : pct.toFixed(2)}%`;
}

/* Response-time sparkline over the same window as the tick bar: a thin teal
   line with a soft area fade, no axes. Down/pending checks break the line
   into segments instead of plotting a fake value. */
function PingSparkline({ beats }: { beats: Beat[] }) {
  const gradientId = useId();
  const W = 400;
  const H = 36;
  const PAD_Y = 4;

  const pings = beats.map(b => (b.status === 1 && b.ping !== null ? b.ping : null));
  const known = pings.filter((p): p is number => p !== null);
  if (known.length < 2) return null;

  const min = Math.min(...known);
  const max = Math.max(...known);
  const span = max - min || 1;
  // Right-align the series so it lines up with the padded tick bar above.
  const offset = TICKS - pings.length;
  const x = (i: number) => ((offset + i + 0.5) / TICKS) * W;
  const y = (p: number) => H - PAD_Y - ((p - min) / span) * (H - PAD_Y * 2);

  const segments: { x: number; y: number }[][] = [];
  let current: { x: number; y: number }[] = [];
  pings.forEach((p, i) => {
    if (p === null) {
      if (current.length > 0) segments.push(current);
      current = [];
    } else {
      current.push({ x: x(i), y: y(p) });
    }
  });
  if (current.length > 0) segments.push(current);

  const line = (pts: { x: number; y: number }[]) =>
    pts.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' ');
  const area = (pts: { x: number; y: number }[]) =>
    `${line(pts)} L${pts[pts.length - 1].x.toFixed(1)},${H} L${pts[0].x.toFixed(1)},${H} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-9"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d6cb" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#34d6cb" stopOpacity="0" />
        </linearGradient>
      </defs>
      {segments.map((pts, i) =>
        pts.length > 1 ? (
          <g key={i}>
            <path d={area(pts)} fill={`url(#${gradientId})`} />
            <path
              d={line(pts)}
              fill="none"
              stroke="#34d6cb"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        ) : null,
      )}
    </svg>
  );
}

/* Per-service card: name + state on top, a full-width bar of recent checks,
   a response-time sparkline over the same window, ping and 24h uptime below.
   Bars pad left with dim ticks until enough history accumulates, so young
   monitors don't render a stub. Surface is settings-card (no backdrop blur:
   Chromium seams wide backdrop-filter elements). */
function ServiceCard({ monitor }: { monitor: MonitorStatus }) {
  const up = monitor.status === 1;
  const beats = monitor.beats.slice(-TICKS);
  const padding = Math.max(0, TICKS - beats.length);

  return (
    <div className="settings-card p-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className={`h-2 w-2 rounded-full flex-shrink-0 ${
              up ? 'bg-success' : monitor.status === 0 ? 'bg-error' : 'bg-warning'
            }`}
          />
          <span className="text-[15px] font-medium text-textPrimary truncate">
            {monitor.name}
          </span>
        </div>
        <span
          className={`text-[12px] font-medium flex-shrink-0 ${
            up ? 'text-success' : monitor.status === 0 ? 'text-error' : 'text-warning'
          }`}
        >
          {up ? 'Operational' : monitor.status === 0 ? 'Down' : 'Pending'}
        </span>
      </div>

      <div className="mt-4 flex items-end gap-[3px]" aria-hidden>
        {Array.from({ length: padding }).map((_, i) => (
          <span key={`pad-${i}`} className="flex-1 h-7 rounded-[2px] bg-white/[0.04]" />
        ))}
        {beats.map((beat, i) => (
          <span
            key={`${beat.time}-${i}`}
            title={`${new Date(beat.time).toLocaleTimeString()}${
              beat.ping !== null ? ` · ${beat.ping} ms` : ''
            }`}
            className={`flex-1 h-7 rounded-[2px] ${beatColor(beat.status)}`}
          />
        ))}
      </div>

      <div className="mt-2">
        <PingSparkline beats={beats} />
      </div>

      <div className="mt-2 flex items-center justify-between text-[12px] text-textMuted tabular-nums">
        <span>{monitor.ping !== null ? `${monitor.ping} ms response` : 'No response data'}</span>
        <span>{formatPct(monitor.uptime24)} uptime · 24h</span>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-12" aria-busy>
      <div className="text-center pt-6">
        <div className="h-6 w-36 rounded-full bg-white/[0.05] animate-pulse mx-auto" />
        <div className="h-14 w-80 max-w-full rounded bg-white/[0.05] animate-pulse mx-auto mt-8" />
        <div className="h-4 w-64 max-w-full rounded bg-white/[0.05] animate-pulse mx-auto mt-6" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="settings-card p-5">
            <div className="h-4 w-32 rounded bg-white/[0.05] animate-pulse" />
            <div className="h-7 rounded bg-white/[0.04] animate-pulse mt-4" />
            <div className="h-3 w-40 rounded bg-white/[0.05] animate-pulse mt-3" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Status() {
  const [data, setData] = useState<StatusPayload | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    fetch('/api/status')
      .then(r => {
        if (!r.ok) throw new Error(`status_${r.status}`);
        return r.json();
      })
      .then(payload => {
        setData(payload as StatusPayload);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) return <LoadingSkeleton />;

  if (error || !data) {
    return (
      <div className="pt-16 text-center">
        <h1 className="font-serif text-5xl font-normal leading-[0.98] text-white sm:text-6xl">
          Status feed <span className="italic">unavailable.</span>
        </h1>
        <p className="mt-5 text-textSecondary max-w-md mx-auto leading-relaxed">
          We couldn't reach the monitoring service. The services themselves may be fine.
        </p>
        <button
          onClick={() => {
            setLoading(true);
            load();
          }}
          className="glass-button mt-8 px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    );
  }

  const monitors = data.groups.flatMap(g => g.monitors);
  const upCount = monitors.filter(m => m.status === 1).length;
  const pings = monitors.map(m => m.ping).filter((p): p is number => p !== null);
  const avgPing = pings.length > 0 ? Math.round(pings.reduce((a, b) => a + b, 0) / pings.length) : null;
  const uptimes = monitors.map(m => m.uptime24).filter((u): u is number => u !== null);
  const avgUptime =
    uptimes.length > 0 ? uptimes.reduce((a, b) => a + b, 0) / uptimes.length : null;
  const headline = HEADLINE[data.overall];

  return (
    <div className="space-y-14 pb-8">
      {/* ─── State hero ───────────────────────────────────────────────── */}
      <section className="relative pt-6 text-center">
        <div className="hero-aurora" aria-hidden="true" />

        <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-[12px] font-medium text-textSecondary">
          <span
            className={`h-1.5 w-1.5 rounded-full ${STATE_COLOR[data.overall]} motion-safe:animate-pulse`}
          />
          {STATE_LABEL[data.overall]} · checked every minute
        </span>

        <h1 className="font-serif mx-auto mt-7 max-w-3xl text-5xl font-normal leading-[0.98] tracking-[-0.01em] text-white sm:text-6xl lg:text-7xl">
          {headline.lead} <span className="italic">{headline.accent}</span>
        </h1>

        <p className="mx-auto mt-5 max-w-xl leading-relaxed text-textSecondary">
          Live health of the StreamNook platform: the website, the API, the bots, and the
          overlay pipeline.
        </p>

        <div className="mt-10 flex items-center justify-center gap-8 text-[13px] tabular-nums">
          <div>
            <p className="text-xl font-semibold text-textPrimary">
              {upCount}<span className="text-textMuted font-normal"> / {monitors.length}</span>
            </p>
            <p className="mt-1 text-textMuted">services up</p>
          </div>
          <div className="h-8 w-px bg-[var(--color-borderLight)]" />
          <div>
            <p className="text-xl font-semibold text-textPrimary">
              {avgPing !== null ? `${avgPing} ms` : '–'}
            </p>
            <p className="mt-1 text-textMuted">avg response</p>
          </div>
          <div className="h-8 w-px bg-[var(--color-borderLight)]" />
          <div>
            <p className="text-xl font-semibold text-textPrimary">{formatPct(avgUptime)}</p>
            <p className="mt-1 text-textMuted">uptime · 24h</p>
          </div>
        </div>
      </section>

      {/* ─── Services ─────────────────────────────────────────────────── */}
      <section className="space-y-10">
        {data.groups.map(group => (
          <div key={group.name}>
            <h2 className="text-sm font-medium text-textSecondary px-1 mb-3">{group.name}</h2>
            <div className={`grid gap-4 ${group.monitors.length > 1 ? 'sm:grid-cols-2' : ''}`}>
              {group.monitors.map(monitor => (
                <ServiceCard key={monitor.name} monitor={monitor} />
              ))}
            </div>
          </div>
        ))}
      </section>

      <p className="text-center text-[12px] text-textMuted tabular-nums">
        Last checked {new Date(data.updatedAt).toLocaleTimeString()}
      </p>
    </div>
  );
}
